/**
 * Builder.io identity-based targeting helpers — PD-34457 Phase 1.
 *
 * Builder.io campaign-page entries are migrating from slug-based lookup to
 * identity-based lookup keyed by:
 *   - `data.companyId` — identifier of the company-entity authoring the page
 *   - `data.pageType`  — which page within an offering's lifecycle this is: TTW | CF
 *
 * Why: a single offering moves through two campaign-page phases (TTW pre-Form-C
 * reservation → CF live raise) that share a slug. Identity lookup lets admin
 * edits target the right phase, and lets us mutate the slug freely (PD-34458)
 * without losing the Builder.io entry.
 *
 * Rollout strategy: this module is additive. Callers that have a companyId
 * + pageType get identity-based queries; callers that only have a slug fall
 * back to the legacy slug query in `BuilderLayoutServer` / `builderContent`.
 * Once all entries are backfilled and admin starts writing both fields on
 * create, the slug fallback can be dropped (tracked separately).
 *
 * PD-35901: collapsed CF1/CF2 → CF. Production data shows each new raise is
 * filed as a fresh `Company` entity with its own `company.externalId`, so the
 * CF1-vs-CF2 distinction was unreachable in practice — `(companyId, CF)` was
 * already unique. Legacy `CF1`/`CF2` values are still accepted as input aliases
 * of `CF` and Builder entries tagged with the legacy values keep rendering via
 * the alias expansion in the lookup queries below (no content migration needed).
 */

export type PageType = 'TTW' | 'CF';

/** All valid page types, in admin/display order. */
export const PAGE_TYPES: PageType[] = ['TTW', 'CF'];

/**
 * Legacy values that map to the current canonical PageType. Kept so existing
 * Builder.io entries tagged `CF1`/`CF2` continue to render, and so existing
 * `?previewPageType=CF1` URLs (admin links, bookmarks, marketing screenshots)
 * keep working. New writes always produce the canonical value (`CF`).
 */
const PAGE_TYPE_ALIASES: Record<PageType, readonly string[]> = {
  TTW: ['TTW'],
  CF: ['CF', 'CF1', 'CF2'],
};

/**
 * All accepted spellings (canonical + legacy) for a `PageType`. Used by the
 * Builder.io query builders to match historical entries that haven't been
 * re-tagged yet, and by `normalizePageType` to accept legacy URL params.
 */
export function pageTypeAliases(pageType: PageType): readonly string[] {
  return PAGE_TYPE_ALIASES[pageType];
}

const LEGACY_TO_CANONICAL = new Map<string, PageType>([
  ['TTW', 'TTW'],
  ['CF', 'CF'],
  ['CF1', 'CF'],
  ['CF2', 'CF'],
]);

/**
 * Coerce an untrusted value (e.g. a `?previewPageType=` query param) into a
 * canonical `PageType`, or `null` when it isn't one. Case-insensitive and
 * whitespace-tolerant. Used by the preview override (PD-35758) so marketing
 * can render a not-yet-live page type while the offering's real status is
 * still something else (e.g. preview the CF page while the offering is TTW).
 *
 * Legacy `CF1` and `CF2` inputs are accepted and mapped to `CF` (PD-35901).
 */
export function normalizePageType(raw: unknown): PageType | null {
  if (typeof raw !== 'string') return null;
  const upper = raw.trim().toUpperCase();
  return LEGACY_TO_CANONICAL.get(upper) ?? null;
}

export interface BuilderIdentity {
  /** Stable company identifier. Preferred lookup key when available. */
  companyId?: string | null;
  /** Which page this is for the company. Required when querying by companyId. */
  pageType?: PageType | null;
  /** URL slug. Legacy fallback when companyId/pageType are not yet available. */
  slug?: string | null;
}

/**
 * Minimal shape this module reads off the V2 offering payload. The real
 * payload (`primary_facade/v2/offerings/:slug`) has many more fields; we
 * intentionally don't tighten the type here so this helper stays usable
 * against any caller's loosely-typed copy.
 *
 * PD-35758: the V2 payload exposes the company identifier under
 * `company.externalId` — the company-entity's primary-platform id. That's the
 * value `/admin/startups` shows as "Company ID" and the value Builder.io
 * entries store in `data.companyId`, so it's preferred first below. (Note
 * `primaryId`/`externalId` at the top level are the per-offering id — a
 * different value — so they are NOT used.)
 *
 * PD-35901: do NOT assume `company.externalId` is stable across an issuer's
 * raises. Production data shows admin's new-offering flow creates a fresh
 * `Company` entity for each raise (verified Jun 2026 on GoSun: `gosun` and
 * `gosun-cf5` carry different `company.externalId` and `accountHolderUserId`).
 * Inside a single raise's TTW → CF transition the id IS stable, which is what
 * the page-type split actually targets. The cross-raise stability claim from
 * the prior comment was incorrect.
 */
export interface OfferingIdentitySource {
  slug?: string | null;
  company?: { id?: string | null; externalId?: string | null } | null;
  companyId?: string | null;
  startupId?: string | null;
  accountHolderUserId?: string | null;
  status?: string | null;
  regulationType?: string | null;
  [key: string]: unknown;
}

/**
 * Extract the stable company identifier the Builder.io entry is keyed on.
 *
 * Preference order (PD-35758):
 *   1. `company.externalId` — the company-entity primary-platform id. This
 *      is what `/admin/startups` displays as "Company ID" and what marketing
 *      enters into Builder.io `data.companyId`. Stable across the TTW → CF
 *      transition within a single raise. NOT stable across separate raises
 *      by the same issuer — admin's new-offering flow currently creates a
 *      fresh `Company` entity per raise (PD-35901).
 *   2. `companyId` / `startupId` — other primary-platform id shapes.
 *   3. `accountHolderUserId` — last-resort proxy (one user per company today).
 *
 * The facade UUID `company.id` is intentionally NOT used — Builder entries are
 * keyed on the primary-platform id, never that UUID. Each candidate is checked
 * for a non-empty trimmed value before falling through (a blank string must
 * not short-circuit the chain).
 *
 * Returns `null` when none are present; callers must then fall back to slug.
 */
export function getCompanyId(
  offeringData: OfferingIdentitySource | null | undefined,
): string | null {
  if (!offeringData) return null;
  const candidates = [
    offeringData.company?.externalId,
    offeringData.companyId,
    offeringData.startupId,
    offeringData.accountHolderUserId,
  ];
  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate.trim().length > 0) {
      return candidate.trim();
    }
  }
  return null;
}

/**
 * Pre-Form-C statuses — these indicate the offering is still in
 * "Test The Waters" mode (collecting interest before a real Reg CF raise).
 *
 * Mirrors the canonical `TTW_BUTTON_STATUSES` in
 * `BuilderLayout/components/shared/offeringStatus.js` (the set that drives
 * the Reserve-Now CTA). `testing` is the value the V2 facade actually emits
 * for live TTW offerings (PD-35758) — omitting it mis-derived TTW pages as
 * CF1. `not_submitted` is retained here (it isn't in the canonical CTA set)
 * for pre-Form-C draft previews that have no `ttwDate` yet.
 */
const TTW_STATUSES = new Set(['testing', 'test_the_waters', 'pre_launch', 'ttw', 'not_submitted']);

/**
 * Derive the page type from offering data.
 *
 * This is a best-effort fallback used until Builder.io entries are backfilled
 * with an authoritative `data.pageType` field. Once the backfill lands, the
 * page render path queries Builder.io directly and this function only
 * supplies a hint for the legacy fallback.
 *
 * Returns `null` when the heuristic can't pick — callers should fall back to
 * slug-only querying in that case.
 *
 * PD-35901: there is no longer a CF1/CF2 distinction (collapsed to CF), so
 * the prior `-cf2` slug heuristic was removed. Marketing slug conventions
 * like `-cf2` / `-cf5` are just naming and do not alter page-type derivation.
 */
export function derivePageType(
  offeringData: OfferingIdentitySource | null | undefined,
): PageType | null {
  if (!offeringData) return null;

  const status = (offeringData.status ?? '').toLowerCase();
  if (TTW_STATUSES.has(status)) return 'TTW';

  const regulation = (offeringData.regulationType ?? '').toLowerCase();
  if (
    regulation.includes('cf') ||
    regulation.includes('reg a') ||
    regulation.includes('reg d') ||
    regulation === '506c'
  ) {
    return 'CF';
  }

  return null;
}

/**
 * Derive a full `BuilderIdentity` from offering data in one call.
 */
export function getBuilderIdentity(
  offeringData: OfferingIdentitySource | null | undefined,
): BuilderIdentity {
  return {
    slug: offeringData?.slug ?? null,
    companyId: getCompanyId(offeringData),
    pageType: derivePageType(offeringData),
  };
}

/**
 * Whether an identity has enough information to query Builder.io by
 * `companyId + pageType` (the new path). When false, callers should fall
 * back to slug-only querying.
 */
export function canUseIdentityLookup(identity: BuilderIdentity | null | undefined): boolean {
  if (!identity) return false;
  return Boolean(identity.companyId) && Boolean(identity.pageType);
}

/** Slug match clauses (tolerant of the leading-slash editor quirk, PD-34728). */
function buildSlugClauses(identity: BuilderIdentity): Array<Record<string, unknown>> {
  const slug = identity.slug ?? '';
  if (!slug) return [];
  const pagePath = `/offering/${slug}`;
  return [
    { 'data.slug': slug },
    { 'data.slug': `/${slug}` },
    { 'data.url': pagePath },
    { 'data.pageUrl': pagePath },
    { url: pagePath },
  ];
}

/**
 * Build the `data.pageType` predicate that matches a canonical page type plus
 * any legacy aliases it absorbed (PD-35901). Returns an exact-equality value
 * when only one spelling exists, otherwise a `$in` clause.
 */
function buildPageTypePredicate(pageType: PageType): string | { $in: readonly string[] } {
  const aliases = pageTypeAliases(pageType);
  return aliases.length === 1 ? aliases[0] : { $in: aliases };
}

/**
 * Exact identity query (`companyId + pageType`). Returns `null` when identity
 * isn't usable. PD-35758: this is queried FIRST and on its own so the exact
 * entry is found regardless of how many slug-sharing entries exist — it never
 * has to compete with slug fallbacks inside a single bounded result window.
 *
 * PD-35901: when `pageType === 'CF'`, the `data.pageType` predicate expands
 * to `$in: ['CF', 'CF1', 'CF2']` so Builder entries that still carry the
 * legacy `CF1` / `CF2` values keep matching without a content migration.
 */
export function buildIdentityCampaignPageQuery(
  identity: BuilderIdentity,
  variantKey?: string | null,
): Record<string, unknown> | null {
  if (!canUseIdentityLookup(identity)) return null;
  const query: Record<string, unknown> = {
    'data.companyId': identity.companyId,
    'data.pageType': buildPageTypePredicate(identity.pageType as PageType),
  };
  if (variantKey) query['data.variantKey'] = variantKey;
  return query;
}

/**
 * Slug-based fallback query, used only when the identity query misses (legacy
 * / not-yet-backfilled entries). Returns `null` when there's no slug.
 *
 * PD-35758: when a page type is known, the slug match is AND-gated to reject
 * *sibling* page types (TTW vs CF) — it accepts a slug entry only when its
 * `data.pageType` is absent (legacy) or equals the requested page type
 * (partial backfill: pageType set, companyId not yet).
 */
export function buildSlugFallbackCampaignPageQuery(
  identity: BuilderIdentity,
  variantKey?: string | null,
): Record<string, unknown> | null {
  const slugClauses = buildSlugClauses(identity);
  if (slugClauses.length === 0) return null;

  let query: Record<string, unknown>;
  if (identity.pageType) {
    const aliases = pageTypeAliases(identity.pageType as PageType);
    const pageTypePredicates: Array<Record<string, unknown>> = [
      { 'data.pageType': { $exists: false } },
      { 'data.pageType': null },
      { 'data.pageType': '' },
      ...aliases.map((alias) => ({ 'data.pageType': alias })),
    ];
    query = { $and: [{ $or: slugClauses }, { $or: pageTypePredicates }] };
  } else {
    query = { $or: slugClauses };
  }
  if (variantKey) query['data.variantKey'] = variantKey;
  return query;
}

/**
 * Combined identity-OR-slug query. Used by the existence check
 * (`hasPublishedBuilderCampaignPage`), where any match is a sufficient boolean.
 * The render and write paths use the two queries above in sequence (identity
 * first, slug fallback on miss) so the exact entry always wins.
 */
export function buildBuilderCampaignPageQuery(
  identity: BuilderIdentity,
  variantKey?: string | null,
): Record<string, unknown> {
  const identityQuery = buildIdentityCampaignPageQuery(identity);
  const slugFallback = buildSlugFallbackCampaignPageQuery(identity);

  const arms = [identityQuery, slugFallback].filter(Boolean) as Array<Record<string, unknown>>;

  let query: Record<string, unknown>;
  if (arms.length === 0) query = {};
  else if (arms.length === 1) query = { ...arms[0] };
  else query = { $or: arms };

  if (variantKey) query['data.variantKey'] = variantKey;
  return query;
}
